import { makeNoise, rand } from './Ridge'

/*
 * ─── Mosaic ───
 * A textured ground for surfaces: a tiling of triangles (the brand's shape), each painted
 * one of a few flat tones picked by a slow noise field, so tones gather into patches the
 * way posterized pixels do. Over it, a coarse grain makes the surface feel printed rather
 * than flat. Deterministic per seed.
 *
 * Palettes are listed light to dark; the noise picks the step.
 */

const PALETTES = {
  ember: ['#f6dcc6', '#f1c3a0', '#ea9c6c', '#e07a45', '#c75f2e'],
  coal: ['#3a3532', '#2e2a27', '#25221f', '#1c1a18', '#e07a45'],
  night: ['#2c2926', '#25221f', '#201e1c', '#1a1917', '#4a3324'],
}

export function Mosaic({ palette = 'ember', seed = 3, size = 64, grain = 0.5, className }) {
  const P = PALETTES[palette]
  const W = 1600
  const H = 1000
  const noise = makeNoise(seed)
  const noise2 = makeNoise(seed + 41)
  const noise3 = makeNoise(seed + 83)
  const r = rand(seed + 7)
  const h = size * 0.866
  const rows = Math.ceil(H / h) + 1
  const cols = Math.ceil(W / (size / 2)) + 2
  const tris = []
  for (let y = 0; y < rows; y++) {
    for (let x = -1; x < cols; x++) {
      const up = (x + y) % 2 === 0
      const x0 = (x * size) / 2
      const y0 = y * h
      const pts = up
        ? `${x0},${y0 + h} ${x0 + size / 2},${y0} ${x0 + size},${y0 + h}`
        : `${x0},${y0} ${x0 + size},${y0} ${x0 + size / 2},${y0 + h}`
      // two noise octaves, posterized into palette steps
      const v = noise(x * 0.11 + y * 0.03) * 0.65 + noise2(y * 0.17 - x * 0.05) * 0.35 + (r() - 0.5) * 0.18
      let i = Math.min(P.length - 2, Math.max(0, Math.floor(v * (P.length - 1))))
      // accents gather where a third noise field peaks, so they arrive in small clusters
      if (palette !== 'ember' && noise3(x * 0.21 + y * 0.29) > 0.86 && r() < 0.6) i = P.length - 1
      tris.push([pts, P[i]])
    }
  }
  const id = `mz${seed}${palette}`
  return (
    <svg className={className} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <filter id={id} x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed={seed} result="n" />
          <feColorMatrix in="n" type="saturate" values="0" />
          <feComponentTransfer>
            <feFuncA type="discrete" tableValues={`0 ${grain}`} />
          </feComponentTransfer>
        </filter>
      </defs>
      {tris.map(([pts, fill], i) => (
        <polygon key={i} points={pts} fill={fill} stroke={fill} strokeWidth="0.8" />
      ))}
      <rect width={W} height={H} filter={`url(#${id})`} style={{ mixBlendMode: 'overlay' }} />
    </svg>
  )
}
